import { aesGcmDecrypt, aesGcmEncrypt } from '@/crypto/aes-gcm'
import { bytesToUtf8, utf8ToBytes } from '@/crypto/encoding'
import { randomIvBytes } from '@/crypto/random'
import {
  createFolder as defaultCreate,
  deleteFolder as defaultDelete,
  listFolders as defaultList,
  updateFolder as defaultUpdate,
} from '../api/folderApi'
import { type Folder, type FolderEncryptedResponse, type VaultCipherBlob } from '../types'

/**
 * Folder の暗号化 / 復号 + REST オーケストレータ (T-D-Vault-Folder、Phase 4)。
 *
 * フォルダ名は AES-256-GCM で暗号化して保管 (ゼロ知識、サーバは名前を知らない)。
 * 暗号化 payload = JSON.stringify({ name }) の UTF-8 bytes。
 */

export interface FolderFlowDeps {
  list: typeof defaultList
  create: typeof defaultCreate
  update: typeof defaultUpdate
  delete: typeof defaultDelete
}

const defaultDeps = (): FolderFlowDeps => ({
  list: defaultList,
  create: defaultCreate,
  update: defaultUpdate,
  delete: defaultDelete,
})

const encryptFolderName = async (
  symmetricKey: Uint8Array,
  name: string,
): Promise<VaultCipherBlob> => {
  const iv = randomIvBytes()
  const plaintext = utf8ToBytes(JSON.stringify({ name }))

  return aesGcmEncrypt(symmetricKey, iv, plaintext)
}

const decryptFolderName = async (
  symmetricKey: Uint8Array,
  cipher: VaultCipherBlob,
): Promise<string> => {
  const plaintextBytes = await aesGcmDecrypt(symmetricKey, cipher)
  const obj = JSON.parse(bytesToUtf8(plaintextBytes)) as Record<string, unknown>

  return typeof obj.name === 'string' ? obj.name : ''
}

const decryptOne = async (
  symmetricKey: Uint8Array,
  resp: FolderEncryptedResponse,
): Promise<Folder | null> => {
  try {
    const name = await decryptFolderName(symmetricKey, resp.cipher)

    return { id: resp.id, name, createdAt: resp.createdAt, updatedAt: resp.updatedAt }
  } catch {
    return null
  }
}

/** GET /vault/folders → 全件復号 → 作成日昇順で返す。復号失敗は skip。 */
export const loadFolderList = async (
  symmetricKey: Uint8Array,
  deps: FolderFlowDeps = defaultDeps(),
): Promise<readonly Folder[]> => {
  const responses = await deps.list()
  const results = await Promise.all(responses.map(r => decryptOne(symmetricKey, r)))

  return results.filter((f): f is Folder => f !== null)
}

/** 新規フォルダを暗号化 → POST → 復号して返す。 */
export const createFolderEncrypted = async (
  symmetricKey: Uint8Array,
  name: string,
  deps: FolderFlowDeps = defaultDeps(),
): Promise<Folder> => {
  const cipher = await encryptFolderName(symmetricKey, name)
  const resp = await deps.create(cipher)
  const decryptedName = await decryptFolderName(symmetricKey, resp.cipher)

  return {
    id: resp.id,
    name: decryptedName,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
  }
}

/** フォルダ名を更新して返す。 */
export const updateFolderEncrypted = async (
  symmetricKey: Uint8Array,
  id: string,
  name: string,
  deps: FolderFlowDeps = defaultDeps(),
): Promise<Folder> => {
  const cipher = await encryptFolderName(symmetricKey, name)
  const resp = await deps.update(id, cipher)
  const decryptedName = await decryptFolderName(symmetricKey, resp.cipher)

  return {
    id: resp.id,
    name: decryptedName,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
  }
}

/** DELETE /vault/folders/:id (204)。関連 VaultItem.folderId は DB で SetNull。 */
export const deleteFolderById = (id: string, deps: FolderFlowDeps = defaultDeps()): Promise<void> =>
  deps.delete(id)
